import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";

type ConnectionFeedbackOptions = {
  statusParam: string;
  errorParam: string;
  successMessage: string;
  errorMessages: Record<string, string>;
  genericError: string;
};

const useExternalAccountsConnectionFeedback = ({
  statusParam,
  errorParam,
  successMessage,
  errorMessages,
  genericError,
}: ConnectionFeedbackOptions) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { reportSuccess, reportError } = useTypedContext(FlashContext);

  // Capture the one-shot redirect params once at mount so the values survive the URL cleanup below.
  const [redirect] = useState(() => ({
    status: searchParams.get(statusParam),
    errorCode: searchParams.get(errorParam),
  }));

  const handledRef = useRef(false);

  const errorMessage = redirect.errorCode
    ? (errorMessages[redirect.errorCode] ?? genericError)
    : undefined;

  useEffect(() => {
    if (handledRef.current || (!redirect.status && !redirect.errorCode)) {
      return;
    }
    handledRef.current = true;

    if (redirect.status === "success") {
      reportSuccess({ message: successMessage });
    }
    if (errorMessage) {
      reportError({ message: errorMessage });
    }

    // Strip the params so a refresh doesn't replay the toast.
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete(statusParam);
    nextParams.delete(errorParam);
    setSearchParams(nextParams, { replace: true });
  }, [
    redirect,
    errorMessage,
    searchParams,
    setSearchParams,
    reportSuccess,
    reportError,
    statusParam,
    errorParam,
    successMessage,
  ]);

  return { errorMessage, errorCode: redirect.errorCode ?? undefined };
};

export default useExternalAccountsConnectionFeedback;
